import React, {useEffect, useRef, useState} from 'react';
import {gql} from '@apollo/client';
import {useMutation, useQuery} from '@apollo/client/react';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import {Close, DirectionsRun, NotificationsNone, People, Route, InfoOutlined} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useLocation, useNavigate} from 'react-router-dom';

dayjs.extend(relativeTime);

const NOTIFICATIONS_QUERY = gql`
  query AdminNotifications {
    notifications {
      id
      type
      message
      isRead
      link
      triggeredBy {
        id
        firstName
        lastName
      }
      createdAt
    }
  }
`;

const MARK_ALL_READ = gql`
  mutation MarkAllNotificationsRead {
    markAllAsRead
  }
`;

const DELETE_NOTIFICATION = gql`
  mutation DeleteNotification($id: ID!) {
    deleteNotification(id: $id)
  }
`;

type NotificationActor = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
};

type AdminNotification = {
  id: string;
  type: string;
  message: string;
  isRead: boolean;
  link?: string | null;
  triggeredBy?: NotificationActor | null;
  createdAt: string;
};

const actorName = (actor?: NotificationActor | null) => {
  const name = `${actor?.firstName ?? ''} ${actor?.lastName ?? ''}`.trim();
  return name || null;
};

const typeLabel = (type: string) => {
  switch (type) {
    case 'ENTRY':
      return 'Entry';
    case 'COMPETITOR':
      return 'Competitor';
    case 'TRACK':
      return 'Track';
    case 'SUCCESS':
      return 'Success';
    case 'WARNING':
      return 'Warning';
    case 'ERROR':
      return 'Error';
    case 'INFO':
      return 'Info';
    default:
      return type || 'Info';
  }
};

const TypeIcon = ({type}: {type: string}) => {
  switch (type) {
    case 'ENTRY':
      return <People fontSize="small" color="primary" />;
    case 'COMPETITOR':
      return <DirectionsRun fontSize="small" color="success" />;
    case 'TRACK':
      return <Route fontSize="small" color="info" />;
    case 'INFO':
    case 'SUCCESS':
    case 'WARNING':
    case 'ERROR':
      return <InfoOutlined fontSize="small" color="action" />;
    default:
      return <InfoOutlined fontSize="small" color="action" />;
  }
};

const NotificationMenu = ({size = 'medium'}: {size?: 'small' | 'medium'}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {data, refetch} = useQuery<{notifications: AdminNotification[]}>(NOTIFICATIONS_QUERY);
  const skipLocationRefetch = useRef(true);

  useEffect(() => {
    if (skipLocationRefetch.current) {
      skipLocationRefetch.current = false;
      return;
    }
    void refetch();
  }, [location.pathname, location.search, refetch]);
  const [markAllAsRead] = useMutation(MARK_ALL_READ);
  const [deleteNotification] = useMutation(DELETE_NOTIFICATION);

  const notifications = data?.notifications ?? [];
  const unreadCount = notifications.filter((notification) => !notification.isRead).length;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (unreadCount > 0) {
      void markAllAsRead().then(() => refetch());
    }
  };

  const handleOpenNotification = (notification: AdminNotification) => {
    handleClose();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleDelete = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    void deleteNotification({variables: {id}}).then(() => refetch());
  };

  return (
    <>
      <IconButton color="inherit" aria-label="notifications" size={size} onClick={handleOpen}>
        <Badge badgeContent={unreadCount} color="error" invisible={unreadCount === 0}>
          <NotificationsNone />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{
          paper: {
            sx: {width: 380, maxWidth: 'calc(100vw - 24px)', maxHeight: 460}
          }
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1}}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          <Button
            size="small"
            disabled={unreadCount === 0}
            onClick={() => {
              void markAllAsRead().then(() => refetch());
            }}
          >
            Mark all read
          </Button>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notification) => {
            const name = actorName(notification.triggeredBy);
            return (
            <MenuItem
              key={notification.id}
              onClick={() => handleOpenNotification(notification)}
              sx={{alignItems: 'flex-start', gap: 1.25, py: 1.25, px: 2, whiteSpace: 'normal', bgcolor: notification.isRead ? 'transparent' : 'action.hover'}}
            >
              <Box sx={{mt: 0.25}}>
                <TypeIcon type={notification.type} />
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {typeLabel(notification.type)}
                </Typography>
                <Typography variant="body2" sx={{whiteSpace: 'normal'}}>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[name ? `by ${name}` : null, dayjs(notification.createdAt).fromNow()].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <IconButton size="small" aria-label="delete notification" onClick={(event) => handleDelete(event, notification.id)}>
                <Close fontSize="small" />
              </IconButton>
            </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;
